import * as React from "react";
import { useEffect } from "react";
import client from "../../lib/sselistener";
import {
  fetchGameOverview,
  gameApiSliceActions,
  gameOverview,
  gameData,
  gameStatus,
  gameViewedPhase,
  loadGameData,
  markBackFromLeft,
} from "../../state/game/game-api-slice";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import getPhaseKey from "../../utils/state/getPhaseKey";
import WDGameProgressOverlay from "../ui/WDGameProgressOverlay";
import WDPreGameWaiting from "../ui/WDPreGameWaiting";
import WDAlertModal from "../ui/WDAlertModal";
import { store } from "../../state/store";

const WDMainController: React.FC = function ({ children }): React.ReactElement {
  const [displayedPhaseKey, setDisplayedPhaseKey] = React.useState<
    string | null
  >(null);
  const dispatch = useAppDispatch();
  const overview = useAppSelector(gameOverview);
  const { data } = useAppSelector(gameData);
  const status = useAppSelector(gameStatus);
  const viewedPhaseState = useAppSelector(gameViewedPhase);

  const countryID = overview.user?.member.countryID;

  const overviewKey = getPhaseKey(overview, "<BAD OVERVIEW_KEY>");
  const statusKey = getPhaseKey(status, "<BAD STATUS_KEY>");
  const dataKey = getPhaseKey(data, "<BAD DATA_KEY>");

  const dispatchFetchOverview = () => {
    const { game } = store.getState();
    /*
     * 首帧时 store 里的 overview.gameID 还是初始值 0；App 会用 URL 里的
     * gameID 发起真正的 loadGame。这里若用 0 再发一次 overview，服务端必然
     * 返回 400，而且它会先占住 PHP 会话锁，让真正的 overview/status/data
     * 请求排队 —— 服务端稍慢时整页就长时间白屏。
     */
    if (game.overview.gameID <= 0) return;
    if (!game.outstandingOverviewRequests) {
      dispatch(
        fetchGameOverview({
          gameID: String(game.overview.gameID),
        }),
      );
    }
  };

  useEffect(() => {
    /*
     * App 已经用 URL 里的 gameID 调用过 loadGame() 并在拉 overview；
     * 这里再发一次只是重复请求（生产上会多花 0.3～1.3 秒、多占一个 PHP worker）。
     * 只在 store 里的 gameID 与 URL 不一致时才补一次，SSE 的 overview
     * 事件与 needsGameOverview 分支仍会照常触发刷新。
     */
    const urlGameID = new URLSearchParams(window.location.search).get("gameID");
    if (overview.gameID > 0 && String(overview.gameID) !== String(urlGameID)) {
      dispatchFetchOverview();
    }
    if (overview.gameID > 0) {
      const overviewChannel = client.subscribe(
        `private-game${overview.gameID}`,
      );

      overviewChannel.bind("overview", async (content) => {
        dispatchFetchOverview();
      });

      overviewChannel.bind("pusher:subscription_succeeded", () => {
        // eslint-disable-next-line no-console
        console.info("overview channel subscription succeeded");
      });

      overviewChannel.bind("pusher:subscription_error", (error) => {
        // eslint-disable-next-line no-console
        console.error("overview channel subscription error", error);
      });
    }
  }, [overview.gameID]);

  const needsGameOverview = useAppSelector(
    ({ game }) => game.needsGameOverview,
  );
  const needsGameData = useAppSelector(({ game }) => game.needsGameData);
  const noPhase = ["Error", "Pre-game"].includes(overview.phase);
  const consistentPhase =
    noPhase || (overviewKey === statusKey && overviewKey === dataKey);

  if (needsGameOverview && !noPhase) {
    dispatch(gameApiSliceActions.setNeedsGameOverview(false));
    dispatch(fetchGameOverview({ gameID: String(overview.gameID) }));
  }
  if (needsGameData && !noPhase) {
    dispatch(gameApiSliceActions.setNeedsGameData(false));
    // uncomment this setTimeout in order to test a slow update
    // setTimeout(() => {
    dispatch(
      loadGameData(
        String(overview.gameID),
        countryID ? String(countryID) : undefined, // keep undefined when converting
      ),
    );
    // }, 1000);
  }

  const { name, gameID } = overview;
  useEffect(() => {
    document.title = `${name} - webDiplomacy`;
  }, [name, gameID]);

  const showOverlay = noPhase || status.status === "Left";
  /*
   * Pre-game 不用那层「Game is waiting to start」的小提示：玩家刚加入时
   * 真正要的是还差几个人、席位都有谁、这局怎么设，这些在
   * WDPreGameWaiting 里。Error 阶段（进不去的局）仍旧用旧提示。
   */
  const showPreGameWaiting = overview.phase === "Pre-game";
  if (displayedPhaseKey === null && overview.phase) {
    setDisplayedPhaseKey(overviewKey);
  }
  return (
    <div>
      {children}
      {showPreGameWaiting && <WDPreGameWaiting overview={overview} />}
      {showOverlay && !showPreGameWaiting && (
        <WDGameProgressOverlay
          overview={overview}
          status={status}
          viewedPhaseState={viewedPhaseState}
          clickHandler={() => {
            setDisplayedPhaseKey(overviewKey);
            // When the user clicks on the overlay, we jump them to the latest phase
            // that they've seen so far. If the game has moved on one or more phases past that
            // then this phase will now be filled with the latest orders that the user has
            // *not* yet seen (i.e. presumably they saw this phase when they were entering
            // orders for it, before that phase ended and other powers' orders appeared).
            dispatch(gameApiSliceActions.setViewedPhaseToLatestPhaseViewed());
            if (status.status === "Left") {
              dispatch(
                markBackFromLeft({
                  countryID: String(countryID),
                  gameID: String(gameID),
                }),
              );
            }
          }}
        />
      )}
      <WDAlertModal />
    </div>
  );
};

export default WDMainController;
